/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * Department Form Modal Component
 * Modal dialog for editing departments
 */

import { Loader2, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useUpdateDepartment } from '@hooks/menu';
import { useAppSelector } from '@hooks/useRedux';
import { useInventoryStockLocations } from '@hooks/inventory/useInventoryStockLocations';
import { selectCurrentUser } from '@/renderer/store/auth/authSelectors';
import { Department, DepartmentStatus } from '../../../types/department';
import { apiError } from '../../../util/notify';
import { DropdownSelect } from '../../common/DropdownSelect';
import { Input } from '../../common/Input';

interface DepartmentFormModalProps {
  isOpen: boolean;
  department?: Department | null;
  onClose: () => void;
}

export const UpdateDepartmentModal: React.FC<DepartmentFormModalProps> = ({
  isOpen,
  onClose,
  department,
}) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [status, setStatus] = useState<DepartmentStatus>('active');
  const [kotEnabled, setKotEnabled] = useState(true);
  const [stockLocationId, setStockLocationId] = useState('');
  const [errors, setErrors] = useState<{ name?: string }>({});
  const updateDepartment = useUpdateDepartment();
  const isLoading = updateDepartment.isPending;

  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;

  const locationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: department?.outlet_id ?? '',
    page: 1,
    pageSize: 200,
    search: '',
    type: 'all',
    status: 'active',
  });
  const stockLocationOptions = useMemo(
    () =>
      (locationsQuery.data?.data ?? []).map((location) => ({
        value: location.id,
        label: location.name,
      })),
    [locationsQuery.data],
  );

  useEffect(() => {
    if (isOpen) {
      if (department) {
        setName(department.name);
        setAddress(department.address || '');
        setStatus(department.status);
        setKotEnabled(department.kot_enabled);
        setStockLocationId(department.stock_location_id || '');
      } else {
        setName('');
        setAddress('');
        setStatus('active');
        setKotEnabled(true);
        setStockLocationId('');
      }
      setErrors({});
    }
  }, [isOpen, department]);

  const validateForm = (): boolean => {
    const newErrors: { name?: string } = {};
    if (!name.trim()) {
      newErrors.name = 'Department name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      apiError('Please fix the errors in the form.');
      return;
    }
    updateDepartment.mutate(
      {
        id: department?.id || '',
        name: name.trim(),
        address: address.trim() || undefined,
        status,
        kot_enabled: kotEnabled,
        stock_location_id: stockLocationId || null,
      },
      { onSuccess: onClose },
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />

      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Edit Department
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label="Department Name"
            value={name}
            onChange={(value) => setName(String(value))}
            placeholder="e.g., Kitchen, Bar, Deli"
            error={errors.name}
            disabled={isLoading}
          />

          <DropdownSelect
            label="Status"
            value={status}
            onChange={(v) => setStatus(v as DepartmentStatus)}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            disabled={isLoading}
          />

          <DropdownSelect
            label="Stock Location (optional)"
            value={stockLocationId}
            onChange={setStockLocationId}
            options={stockLocationOptions}
            placeholder={
              locationsQuery.isLoading
                ? 'Loading stock locations…'
                : 'Select the location this department sells from'
            }
            isLoadingOptions={locationsQuery.isLoading}
            disabled={isLoading}
            searchable={stockLocationOptions.length > 10}
          />

          <label
            htmlFor="department-kot-toggle-edit"
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className="relative">
              <input
                id="department-kot-toggle-edit"
                type="checkbox"
                checked={kotEnabled}
                onChange={(e) => setKotEnabled(e.target.checked)}
                disabled={isLoading}
                className="sr-only peer"
              />
              <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-pos-primary transition-colors" />
              <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm peer-checked:translate-x-5 transition-transform" />
            </div>
            <span className="text-sm text-gray-700">
              Print KOT for this department
            </span>
          </label>

          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-pos-primary text-white rounded-lg hover:bg-pos-secondary transition-colors disabled:opacity-50"
            >
              {isLoading && <Loader2 size={18} className="animate-spin" />}
              <span>Update</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
